import { loadModules, loadUnit } from '../data/content-loader';
import { progressStore } from '../engine/progress';
import type { ModuleCatalogEntry, Unit, UnitCatalogEntry } from '../types';

type ProgressRailElement = HTMLElement & { setProgress?: (current: number, total: number) => void };

interface SidebarShell {
  app: HTMLElement;
  list: HTMLElement;
  rail: ProgressRailElement;
  progressLabel: HTMLElement;
  menuToggle: HTMLButtonElement;
  closeButton: HTMLButtonElement;
  scrim: HTMLElement;
}

export class Sidebar {
  private modules: ModuleCatalogEntry[] = [];
  private currentUnitId: string | null = null;
  private currentSlideId: string | null = null;
  private openModule: string | null = null;

  /** Units whose slide list is expanded in the sidebar. */
  private expandedUnitIds = new Set<string>();
  /** Cache of loaded unit data, keyed by unit id. */
  private loadedUnits = new Map<string, Unit>();

  constructor(private readonly shell: SidebarShell) {
    shell.menuToggle.addEventListener('click', () => this.toggle(true));
    shell.closeButton.addEventListener('click', () => this.toggle(false));
    shell.scrim.addEventListener('click', () => this.toggle(false));
    progressStore.addEventListener('change', () => this.render());
    window.addEventListener('hashchange', () => {
      void this.syncFromHash();
    });
  }

  async start(): Promise<void> {
    this.modules = await loadModules();
    this.openModule = this.modules[0]?.id ?? null;
    await this.syncFromHash();
  }

  setCurrentUnit(unitId: string): void {
    this.currentUnitId = unitId;
    const module = this.modules.find((m) => m.units.some((u) => u.id === unitId));
    if (module) this.openModule = module.id;
    this.render();
  }

  private async syncFromHash(): Promise<void> {
    const match = /^#\/(m1|m2|m3)\/u(\d{2})\/s(\d{2})/.exec(location.hash);
    if (!match) {
      this.currentUnitId = null;
      this.currentSlideId = null;
      this.render();
      return;
    }

    const moduleId = match[1] as 'm1' | 'm2' | 'm3';
    const unitIndex = Number(match[2]);
    const unitId = `${moduleId}-u${match[2]}`;
    this.currentUnitId = unitId;
    this.currentSlideId = `${unitId}-s${match[3]}`;
    this.openModule = moduleId;

    // Auto-expand the current unit and load its data if needed.
    this.expandedUnitIds.add(unitId);
    if (!this.loadedUnits.has(unitId)) {
      const unit = await loadUnit(moduleId, unitIndex);
      this.loadedUnits.set(unitId, unit);
    }

    this.render();
  }

  private toggle(open: boolean): void {
    this.shell.app.dataset.sidebarOpen = String(open);
  }

  private render(): void {
    if (!this.modules.length) return;

    // Sidebar shows the current module only — keeps focus on what the
    // learner is actually working through.
    const current = this.modules.find((m) => m.id === this.openModule) ?? this.modules[0];
    if (!current) return;

    const totals = this.computeTotals(current);
    this.shell.rail.setProgress?.(totals.completed, Math.max(totals.total, 1));
    const pct = totals.total ? Math.round((totals.completed / totals.total) * 100) : 0;
    this.shell.progressLabel.textContent = `${pct}% complete · ${totals.completed} / ${totals.total} slides`;

    this.shell.list.replaceChildren();

    const heading = document.createElement('p');
    heading.className = 'current-module-title';
    heading.textContent = current.title;
    this.shell.list.append(heading);

    if (!current.units.length) {
      const empty = document.createElement('p');
      empty.className = 'module-empty';
      empty.textContent = `${current.description} ${current.totalSlides.toLocaleString()} slides planned.`;
      this.shell.list.append(empty);
      return;
    }

    const units = document.createElement('div');
    units.className = 'unit-stack';
    for (const unit of current.units) units.append(this.renderUnit(unit));
    this.shell.list.append(units);
  }

  private renderUnit(unit: UnitCatalogEntry): HTMLElement {
    const group = document.createElement('div');
    group.className = 'unit-group';

    const isExpanded = this.expandedUnitIds.has(unit.id);
    const isCurrent = this.currentUnitId === unit.id;

    const ratio = progressStore.progressRatio(unit.id, unit.slides);
    const state = ratio.completed >= unit.slides ? 'complete' : ratio.completed > 0 ? 'partial' : 'empty';

    // Single button row: title + ratio + chevron. One control, one action.
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'unit';
    row.dataset.unitId = unit.id;
    row.dataset.progress = state;
    row.setAttribute('aria-expanded', String(isExpanded));
    if (isCurrent) row.setAttribute('aria-current', 'true');

    row.innerHTML = `
      <span class="unit-check" data-state="${state}" aria-hidden="true">
        ${state === 'complete'
          ? '<svg viewBox="0 0 16 16"><path d="M4 8.5l2.5 2.5L12 5.5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : ''}
      </span>
      <span class="unit-title">${escapeHtml(unit.title)}</span>
      <span class="unit-progress">${ratio.completed}/${unit.slides}</span>
      <svg class="unit-chev" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.75" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
    `;
    row.addEventListener('click', () => void this.toggleUnitExpand(unit));
    group.append(row);

    if (isExpanded) {
      const cached = this.loadedUnits.get(unit.id);
      if (cached) {
        group.append(this.renderSlideList(cached));
      } else {
        const loading = document.createElement('p');
        loading.className = 'unit-slides-loading';
        loading.textContent = 'Loading slides…';
        group.append(loading);
      }
    }

    return group;
  }

  private async toggleUnitExpand(unit: UnitCatalogEntry): Promise<void> {
    if (this.expandedUnitIds.has(unit.id)) {
      this.expandedUnitIds.delete(unit.id);
      this.render();
      return;
    }

    this.expandedUnitIds.add(unit.id);
    this.render(); // Show loading state immediately.

    if (!this.loadedUnits.has(unit.id)) {
      try {
        const [moduleId] = unit.id.split('-') as ['m1' | 'm2' | 'm3'];
        const unitData = await loadUnit(moduleId, unit.index);
        this.loadedUnits.set(unit.id, unitData);
      } catch {
        this.expandedUnitIds.delete(unit.id);
      }
      this.render();
    }
  }

  private renderSlideList(unit: Unit): HTMLElement {
    const nav = document.createElement('nav');
    nav.className = 'slide-nav';
    nav.setAttribute('aria-label', `Slides in ${unit.title}`);

    for (const slide of unit.slides) {
      const link = document.createElement('a');
      link.className = 'slide-link';
      link.href = `#/${unit.moduleId}/u${pad2(unit.index)}/s${pad2(slide.index)}`;
      if (this.currentSlideId === slide.id) link.setAttribute('aria-current', 'true');
      const progress = progressStore.getSlide(unit.id, slide.id);
      link.dataset.progress = progress?.completed ? 'complete' : progress?.visited ? 'partial' : 'empty';
      link.innerHTML = `
        <span class="slide-link-check" data-state="${link.dataset.progress}" aria-hidden="true">
          ${progress?.completed
            ? '<svg viewBox="0 0 16 16"><path d="M4 8.5l2.5 2.5L12 5.5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : ''}
        </span>
        <span class="slide-link-title">${escapeHtml(slide.title)}</span>
      `;
      link.addEventListener('click', () => this.toggle(false));
      nav.append(link);
    }

    return nav;
  }

  private computeTotals(module: ModuleCatalogEntry): { completed: number; total: number } {
    let completed = 0;
    const total = module.totalSlides;
    for (const unit of module.units) {
      completed += progressStore.progressRatio(unit.id, unit.slides).completed;
    }
    return { completed, total };
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

